import { isChannelLocked } from '../../shared/communityUtils';
import { IChannel } from '../../types/communityTypes';
import { UAMPermissions } from '../../types/userTypes';
import { setUserCommunityAccess } from '../communityReducer';
import { AppThunk } from '../store';
import { checkForPermissions } from '../userReducer';

export const updateUserCommunityAccess =
  (): AppThunk => async (dispatch, getState) => {
    const {
      isTagMango,
      badges: allBadges,
      isCommunityEnabled,
      hostMetadata,
    } = getState().app;
    const {
      id: userId,
      type: userType,
      userAccessPermissions,
      badges: userBadges,
      isBlockedFromCommunityEngagement,
    } = getState().user;
    const { allCreatorMangoes, subscribedMangoes } = getState().mango;
    const { groupChannelList } = getState().community;

    // user has access to the community if they are a creator or have the required permissions as UAM
    const hasCommunityCreatorAccess =
      userType === 'creator_completed' ||
      (userType === 'creator_restricted' &&
        checkForPermissions(
          [UAMPermissions.COMMUNITY_ACCESS],
          userAccessPermissions,
        ));

    // user can post in mangoes if they are a creator or have the required permissions as UAM or customer not blocked from community engagement and has access to community enabled mangoes (hostData.communityEnabledMangoes) for creating posts
    const canPostInMangoes = Boolean(
      (isTagMango
        ? userType === 'creator_completed' || userType === 'dual'
        : userId === hostMetadata.creator._id) ||
        (userType === 'creator_restricted' &&
          checkForPermissions(
            [UAMPermissions.FEED_WRITE],
            userAccessPermissions,
          )) ||
        (userType === 'fan_completed' &&
          isCommunityEnabled &&
          !isBlockedFromCommunityEngagement),
    );

    // user can post in a channel if they have any of the required badges or mangoes for that channel or they have creator access
    const accessChannelsToPost: IChannel[] = [];
    const canPostInChannels =
      hasCommunityCreatorAccess ||
      groupChannelList.reduce((acc, group) => {
        // filter channels only if it has feed enabled and allowMemberPosts set to true and is not locked for the user
        const channels = group.channelArr
          .filter(
            (ch) =>
              ch.features.includes('feed') &&
              ch.featureSettings.feed.allowMemberPosts,
          )
          .filter((ch) => !isChannelLocked(ch.mangoArr, ch.badgeArr));

        accessChannelsToPost.push(...channels.map((ch) => ch));

        return acc || channels.length > 0;
      }, false);

    const accessChannels = groupChannelList.reduce((acc, group) => {
      const channels = hasCommunityCreatorAccess
        ? group.channelArr
        : group.channelArr.filter(
            (ch) => !isChannelLocked(ch.mangoArr, ch.badgeArr),
          );

      return [...acc, ...channels];
    }, [] as IChannel[]);

    dispatch(
      setUserCommunityAccess({
        communityCreator: hostMetadata.creator._id || '',
        hasCommunityCreatorAccess,
        canPostInMangoes,
        canPostInChannels,
        accessChannelsToPost: hasCommunityCreatorAccess
          ? accessChannels
          : accessChannelsToPost,
        accessChannels,
        accessMangoes: hasCommunityCreatorAccess
          ? allCreatorMangoes
          : subscribedMangoes,
        accessBadges: hasCommunityCreatorAccess
          ? allBadges
          : allBadges.filter((ab) => userBadges?.some((b) => b === ab._id)),
      }),
    );
  };
